'use client';

import { Suspense, useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/Toast';
import { Button, Input, Select, Checkbox, Spinner } from '@/components/ui';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const GENDER_OPTIONS = [
  { value: '', label: '성별 선택' },
  { value: 'male', label: '남성' },
  { value: 'female', label: '여성' },
];

function WorkerSignupForm() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isKakao, setIsKakao] = useState(false);
  const [kakaoId, setKakaoId] = useState('');
  const [formData, setFormData] = useState({
    // Step 1: Basic info
    email: '',
    password: '',
    passwordConfirm: '',
    phone: '',
    // Step 2: Personal info
    name: '',
    gender: '',
    residentNoFront: '',
    residentNoBackEnc: '',
    // Step 3: Account info
    bankName: '',
    accountNo: '',
    accountHolder: '',
    // Agreements
    agreeAll: false,
    agreePrivacy: false,
    agreeThirdParty: false,
    agreeMarketing: false,
  });
  const [errors, setErrors] = useState({});

  const { signup } = useAuth();
  const toast = useToast();
  const router = useRouter();
  const searchParams = useSearchParams();

  // 카카오 프리필
  useEffect(() => {
    if (searchParams.get('from') !== 'kakao') return;
    try {
      const raw = document.cookie
        .split('; ')
        .find(c => c.startsWith('kakaoProfile='));
      if (!raw) return;
      const profile = JSON.parse(decodeURIComponent(raw.split('=').slice(1).join('=')));
      setIsKakao(true);
      setKakaoId(profile.kakaoId || '');
      setFormData(prev => ({
        ...prev,
        email: profile.email || prev.email,
        name: profile.name || prev.name,
      }));
      // 쿠키 삭제
      document.cookie = 'kakaoProfile=; path=/; max-age=0';
    } catch {
      // 파싱 실패 무시
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleAgreeAll = (e) => {
    const { checked } = e.target;
    setFormData(prev => ({
      ...prev,
      agreeAll: checked,
      agreePrivacy: checked,
      agreeThirdParty: checked,
      agreeMarketing: checked,
    }));
  };

  const validateStep = (stepNum) => {
    const newErrors = {};

    if (stepNum === 1) {
      if (!formData.email) newErrors.email = '이메일을 입력해주세요.';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = '올바른 이메일 형식이 아닙니다.';

      if (!isKakao) {
        if (!formData.password) newErrors.password = '비밀번호를 입력해주세요.';
        else if (formData.password.length < 8) newErrors.password = '비밀번호는 8자 이상이어야 합니다.';

        if (formData.password !== formData.passwordConfirm) {
          newErrors.passwordConfirm = '비밀번호가 일치하지 않습니다.';
        }
      }

      if (!formData.phone) newErrors.phone = '휴대폰 번호를 입력해주세요.';
    }

    if (stepNum === 2) {
      if (!formData.name) newErrors.name = '이름을 입력해주세요.';
      if (!formData.residentNoFront || formData.residentNoFront.length !== 6) {
        newErrors.residentNoFront = '주민번호 앞자리 6자리를 입력해주세요.';
      }
      if (!formData.gender) newErrors.gender = '성별을 선택해주세요.';
    }

    if (stepNum === 3) {
      if (!formData.agreePrivacy) newErrors.agreePrivacy = '개인정보 처리방침에 동의해주세요.';
      if (!formData.agreeThirdParty) newErrors.agreeThirdParty = '제3자 정보제공에 동의해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setLoading(true);

    try {
      // 주민번호 앞자리에서 생년월일 추출
      const front = formData.residentNoFront;
      const yy = front.substring(0, 2);
      const mm = front.substring(2, 4);
      const dd = front.substring(4, 6);
      const backFirst = formData.residentNoBackEnc?.[0];
      const century = (backFirst === '3' || backFirst === '4') ? '20' : '19';
      const birthDate = `${century}${yy}-${mm}-${dd}`;

      await signup({
        email: formData.email,
        ...(isKakao ? { kakaoId } : { password: formData.password }),
        phone: formData.phone,
        role: 'worker',
        name: formData.name,
        birthDate,
        gender: formData.gender,
        residentNoFront: formData.residentNoFront,
        residentNoBackEnc: formData.residentNoBackEnc,
        bankName: formData.bankName,
        accountNo: formData.accountNo,
        accountHolder: formData.accountHolder || formData.name,
        agreePrivacy: formData.agreePrivacy,
        agreeThirdParty: formData.agreeThirdParty,
        agreeMarketing: formData.agreeMarketing,
      });

      toast.success('회원가입이 완료되었습니다!');
      router.push('/login');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (step < 3) {
      handleNext();
    } else {
      handleSubmit();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="p-6 flex items-center gap-4 border-b bg-white">
        <button
          onClick={step > 1 ? handleBack : () => router.back()}
          className="p-2 -ml-2 hover:bg-slate-100 rounded-xl"
        >
          <ArrowLeftIcon className="w-6 h-6 text-slate-600" />
        </button>
        <h1 className="text-lg font-semibold text-slate-800">구직자 회원가입</h1>
      </div>

      {/* Progress */}
      <div className="px-6 py-4 bg-white border-b">
        <div className="flex items-center gap-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex-1 flex items-center gap-2">
              <div className={`flex-1 h-1 rounded-full ${s <= step ? 'bg-green-500' : 'bg-slate-200'}`} />
            </div>
          ))}
        </div>
        <p className="text-sm text-slate-500 mt-2">
          {step}/3 단계 - {step === 1 ? '계정 정보' : step === 2 ? '기본 정보' : '약관 동의'}
        </p>
      </div>

      {/* Content */}
      <form onSubmit={handleFormSubmit} className="flex-1 flex flex-col">
        <div className="flex-1 p-6">
          <div className="max-w-md mx-auto space-y-4">
          {/* Step 1: Account Info */}
          {step === 1 && (
            <>
              {isKakao && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-sm text-yellow-800">
                  카카오 계정으로 가입합니다. 추가 정보를 입력해주세요.
                </div>
              )}
              <Input
                label="이메일"
                type="email"
                name="email"
                placeholder="example@email.com"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                disabled={isKakao && !!formData.email}
                required
              />
              {!isKakao && (
                <>
                  <Input
                    label="비밀번호"
                    type="password"
                    name="password"
                    placeholder="8자 이상"
                    value={formData.password}
                    onChange={handleChange}
                    error={errors.password}
                    required
                  />
                  <Input
                    label="비밀번호 확인"
                    type="password"
                    name="passwordConfirm"
                    placeholder="비밀번호 재입력"
                    value={formData.passwordConfirm}
                    onChange={handleChange}
                    error={errors.passwordConfirm}
                    required
                  />
                </>
              )}
              <Input
                label="휴대폰 번호"
                type="tel"
                name="phone"
                placeholder="010-0000-0000"
                value={formData.phone}
                onChange={handleChange}
                error={errors.phone}
                required
              />
            </>
          )}

          {/* Step 2: Personal Info */}
          {step === 2 && (
            <>
              <Input
                label="이름"
                type="text"
                name="name"
                placeholder="실명 입력"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
                required
              />
              <Select
                label="성별"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                options={GENDER_OPTIONS}
                error={errors.gender}
                required
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="주민번호 앞자리"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  name="residentNoFront"
                  placeholder="000000"
                  maxLength={6}
                  value={formData.residentNoFront}
                  onChange={handleChange}
                  error={errors.residentNoFront}
                  required
                />
                <Input
                  label="뒷자리"
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  name="residentNoBackEnc"
                  placeholder="0000000"
                  maxLength={7}
                  value={formData.residentNoBackEnc}
                  onChange={handleChange}
                />
              </div>
            </>
          )}

          {/* Step 3: Agreements */}
          {step === 3 && (
            <>
              <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                <Checkbox
                  name="agreeAll"
                  label="전체 동의"
                  checked={formData.agreeAll}
                  onChange={handleAgreeAll}
                  className="pb-3 border-b border-slate-100"
                />
                <Checkbox
                  name="agreePrivacy"
                  label="[필수] 개인정보 처리방침 동의"
                  checked={formData.agreePrivacy}
                  onChange={handleChange}
                  error={errors.agreePrivacy}
                />
                <Checkbox
                  name="agreeThirdParty"
                  label="[필수] 제3자 정보제공 동의"
                  checked={formData.agreeThirdParty}
                  onChange={handleChange}
                  error={errors.agreeThirdParty}
                />
                <Checkbox
                  name="agreeMarketing"
                  label="[선택] 마케팅 정보 수신 동의"
                  checked={formData.agreeMarketing}
                  onChange={handleChange}
                />
              </div>
            </>
          )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-white border-t">
          <div className="max-w-md mx-auto">
            {step < 3 ? (
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full bg-green-500 hover:bg-green-600"
              >
                다음
              </Button>
            ) : (
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full bg-green-500 hover:bg-green-600"
                disabled={loading}
              >
                {loading ? <Spinner size="sm" /> : '가입 완료'}
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

export default function WorkerSignupPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-slate-50 flex items-center justify-center"><Spinner /></div>}>
      <WorkerSignupForm />
    </Suspense>
  );
}
